/**
 * @file tables.js
 * @description Default table column configurations used across the application.
 * Each exported constant contains an array of column definitions used by table
 * components to render headers, widths, classes and per-column settings.
 */

/**
 * Reservation table default column definitions.
 * Used to render reservation lists with default visibility and ordering.
 * @type {Object[]}
 */
export const RESERVATION_TABLE_DEFAULTS = [
  {
    key: "selected",
    label: "",
    sortable: false,
    class: "text-center",
    thStyle: { minWidth: "30px" },
    stickyColumn: true,
    settings: {
      drag: false,
      disabled: true,
      default: true,
    },
  },
  {
    key: "booking_number",
    label: "Reservation N° / ID",
    sortable: true,
    thStyle: { minWidth: "180px" },
    class: "text-left",
    stickyColumn: true,
    settings: {
      drag: false,
      disabled: true,
      default: true,
    },
  },
  {
    key: "typeTransfer",
    label: "Transfer Type",
    sortable: true,
    thStyle: { minWidth: "140px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "reservationtype",
    label: "Reservation Type",
    sortable: true,
    thStyle: { minWidth: "140px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "from",
    label: "From",
    sortable: true,
    thStyle: { minWidth: "320px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "to",
    label: "To",
    sortable: true,
    thStyle: { minWidth: "320px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "experienceTitle",
    label: "Experience Title",
    sortable: true,
    thStyle: { minWidth: "320px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "pickup_date",
    label: "Date & Time",
    sortable: true,
    thStyle: { minWidth: "120px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "passenger",
    label: "PAX",
    sortable: true,
    thStyle: { minWidth: "150px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "passengersCount",
    label: "PAX N°",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "transportRef",
    label: "Transport Ref.",
    sortable: true,
    thStyle: { minWidth: "120px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "customer",
    label: "Customer",
    sortable: true,
    thStyle: { minWidth: "150px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },

  {
    key: "service",
    label: "Service",
    sortable: true,
    thStyle: { minWidth: "220px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "supplier",
    label: "Destination / Supplier",
    sortable: true,
    thStyle: { minWidth: "190px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "driver",
    label: "Driver",
    sortable: true,
    thStyle: { minWidth: "120px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },

  {
    key: "referredBy",
    label: "Referral",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },

  {
    key: "quotedPrice",
    label: "Quoted Price",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "netPrice",
    label: "Net Price",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "grossPrice",
    label: "Gross Price",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "netProfit",
    label: "Net Profit",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "totalCharge",
    label: "Total Charge",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "totalExtras",
    label: "Extra Charges",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "totalDiscounts",
    label: "Discounts",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "stripeFee",
    label: "Stripe Fee",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "commission",
    label: "Commission",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "reservation_reference",
    label: "Reserv. Ref.",
    sortable: true,
    thStyle: { minWidth: "160px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "deal_number",
    label: "Zoho Deal N°",
    sortable: true,
    thStyle: { minWidth: "160px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },

  {
    key: "invoice_n",
    label: "Invoice N°",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "payment_reference",
    label: "Payment Ref.",
    sortable: true,
    thStyle: { minWidth: "160px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "source_channel",
    label: "Origin",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "source_provider",
    label: "Provider",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "payment",
    label: "Payment",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "status",
    label: "Status",
    sortable: true,
    thStyle: { minWidth: "140px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },

  {
    key: "actions",
    label: "Actions",
    sortable: false,
    class: "alinmov6",
    thStyle: { minWidth: "90px" },
    stickyColumn: false,
    settings: {
      drag: false,
      disabled: true,
      default: true,
    },
  },
];

/**
 * Experiences table default column definitions.
 * @type {Object[]}
 */
export const EXPERIENCES_TABLE_DEFAULTS = [
  {
    key: "selected",
    label: "",
    sortable: false,
    class: "text-center",
    thStyle: { minWidth: "40px" },
    stickyColumn: true,
    settings: {
      drag: false,
      disabled: true,
      default: true,
    },
  },
  {
    key: "productCode",
    label: "Code",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left",
    stickyColumn: true,
    settings: {
      drag: false,
      disabled: true,
      default: true,
    },
  },
  {
    key: "title",
    label: "Title",
    sortable: true,
    thStyle: { minWidth: "400px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: true,
      default: true,
    },
  },
  {
    key: "destination",
    label: "Destination / Supplier",
    sortable: true,
    thStyle: { minWidth: "190px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "category",
    label: "Category",
    sortable: true,
    thStyle: { minWidth: "120px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "vehicle",
    label: "Vehicle",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "seats",
    label: "N° Seats",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "transferType",
    label: "Transfer",
    sortable: true,
    thStyle: { minWidth: "80px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "basePrice",
    label: "Price",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "status",
    label: "Status",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "areaCode",
    label: "Area",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "availability",
    label: "Availability",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "pricingMethod",
    label: "Pricing Method",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },

  {
    key: "quote",
    label: "Quote",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "cutoff",
    label: "Cut Off",
    sortable: true,
    thStyle: { minWidth: "130px" },
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "actions",
    label: "Actions",
    sortable: false,
    class: "alinmov6",
    thStyle: { minWidth: "90px" },
    stickyColumn: false,
    settings: {
      drag: false,
      disabled: true,
      default: true,
    },
  },
];

/**
 * Rate table default column definitions.
 * Renamed to plural (RATE_TABLE_DEFAULTS) to match other *_DEFAULTS naming.
 * @type {Object[]}
 */
export const RATE_TABLE_DEFAULTS = [
  {
    key: "selected",
    label: "",
    sortable: false,
    class: "text-center",
    thStyle: { minWidth: "40px" },
    stickyColumn: true,
    settings: {
      drag: false,
      disabled: true,
      default: true,
    },
  },
  {
    key: "category",
    label: "Vehicle",
    sortable: true,
    thStyle: { minWidth: "160px" },
    class: "text-left",
    stickyColumn: true,
    settings: {
      drag: false,
      disabled: true,
      default: true,
    },
  },
  {
    key: "destination",
    label: "Supplier / Destination",
    sortable: true,
    thStyle: { minWidth: "190px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: true,
      default: true,
    },
  },
  {
    key: "pricingSelections",
    label: "Pricing Selections",
    sortable: true,
    thStyle: { minWidth: "120px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "seats",
    label: "N° Seats",
    sortable: true,
    thStyle: { minWidth: "80px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "childSeats",
    label: "N° Child Seats",
    sortable: true,
    thStyle: { minWidth: "80px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "childSeatPrice",
    label: "Child Seat Price",
    sortable: true,
    thStyle: { minWidth: "80px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "areas",
    label: "Areas",
    sortable: true,
    thStyle: { minWidth: "50px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "especialConditions",
    label: "Especial Cond.",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "minbasePriceByArea",
    label: "Price",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "priceByDistance",
    label: "Price Extra KM",
    sortable: true,
    thStyle: { minWidth: "110px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "validity",
    label: "Validity",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "status",
    label: "Status",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "basePrice",
    label: "Base Price",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "currency",
    label: "Currency",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "minimumDisposition",
    label: "Hourly Minimum",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "maximumDisposition",
    label: "Hourly Maximum",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "distanceInclude",
    label: "Km inc. x hour",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "priceByDisposition",
    label: "Price x hour",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "meetAndGreetPrice",
    label: "Meet & Greet Price",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },

  {
    key: "actions",
    label: "Actions",
    sortable: false,
    class: "alinmov6",
    thStyle: { minWidth: "90px" },
    stickyColumn: false,
    settings: {
      drag: false,
      disabled: true,
      default: true,
    },
  },
];

/**
 *  quotes table default column definitions.
 * Used to render quotes lists with default visibility and ordering.
 * @type {Object[]}
 */
export const QUOTE_TABLE_DEFAULTS = [
  {
    key: "selected",
    label: "",
    sortable: false,
    class: "text-center",
    thStyle: { minWidth: "30px" },
    stickyColumn: true,
    settings: {
      drag: false,
      disabled: true,
      default: true,
    },
  },
  {
    key: "quoteNumber",
    label: "Quote N° / ID",
    sortable: true,
    thStyle: { minWidth: "180px" },
    class: "text-left",
    stickyColumn: true,
    settings: {
      drag: false,
      disabled: true,
      default: true,
    },
  },
  {
    key: "transferType",
    label: "Transfer Type",
    sortable: true,
    thStyle: { minWidth: "140px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "classQuote",
    label: "Reservation Type",
    sortable: true,
    thStyle: { minWidth: "140px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "from",
    label: "From",
    sortable: true,
    thStyle: { minWidth: "320px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "to",
    label: "To",
    sortable: true,
    thStyle: { minWidth: "320px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },

  {
    key: "dateAndTime",
    label: "Date & Time",
    sortable: true,
    thStyle: { minWidth: "120px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "passenger",
    label: "PAX",
    sortable: true,
    thStyle: { minWidth: "150px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },
  {
    key: "passengersCount",
    label: "PAX N°",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "transportRef",
    label: "Transport Ref.",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "customer",
    label: "Customer",
    sortable: true,
    thStyle: { minWidth: "150px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },

  {
    key: "service",
    label: "Service",
    sortable: true,
    thStyle: { minWidth: "220px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "supplier",
    label: "Destination / Supplier",
    sortable: true,
    thStyle: { minWidth: "190px" },
    class: "text-left",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },

  {
    key: "netPrice",
    label: "Net Price",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "grossPrice",
    label: "Gross Price",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "netProfit",
    label: "Net Profit",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "totalCharge",
    label: "Total Charge",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "totalExtras",
    label: "Extra Charges",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "totalDiscounts",
    label: "Discounts",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },

  {
    key: "commissions",
    label: "Commission",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "dealNumber",
    label: "Zoho Deal N°",
    sortable: true,
    thStyle: { minWidth: "160px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },

  {
    key: "sourceChannel",
    label: "Origin",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },
  {
    key: "sourceProvider",
    label: "Provider",
    sortable: true,
    thStyle: { minWidth: "130px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: false,
    },
  },

  {
    key: "status",
    label: "Status",
    sortable: true,
    thStyle: { minWidth: "140px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true,
    },
  },

  {
    key: "actions",
    label: "Actions",
    sortable: false,
    class: "alinmov6",
    thStyle: { minWidth: "90px" },
    stickyColumn: false,
    settings: {
      drag: false,
      disabled: true,
      default: true,
    },
  },
];

/** Passenger view table column definitions.
 * Used to render passenger lists in reservation details.
 * @type {Object[]}
 */
export const FIELDS_PASSENGER_TABLE = [
  {
    key: "fullname",
    label: "Full Name",
    sortable: true,
    thStyle: { minWidth: "150px" },
    class: "text-left"
  },
  {
    key: "phone",
    label: "Phone",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left"
  },
  {
    key: "email",
    label: "Email",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left"
  },
  {
    key: "type",
    label: "Type",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center"
  },
  {
    key: "itinerary",
    label: "Flight N°",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center"
  },
  {
    key: "notify",
    label: "Notifications",
    sortable: true,
    thStyle: { minWidth: "120px" },
    class: "text-center"
  },
  {
    key: "actions",
    label: "Actions",
    sortable: false,
    thStyle: { minWidth: "120px" },
    class: "text-center"
  }
];

/** Passenger view table column definitions without actions.
 * Used to render passenger lists in reservation view mode.
 * @type {Object[]}
 */
export const FIELDS_PASSENGER = [
  {
    key: "fullname",
    label: "Full Name",
    sortable: true,
    thStyle: { minWidth: "150px" },
    class: "text-left"
  },
  {
    key: "phone",
    label: "Phone",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left"
  },
  {
    key: "email",
    label: "Email",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left"
  },
  {
    key: "type",
    label: "Type",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center"
  },
  {
    key: "itinerary",
    label: "Flight N°",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center"
  },
  {
    key: "notify",
    label: "Notifications",
    sortable: true,
    thStyle: { minWidth: "120px" },
    class: "text-center"
  },
  {
    key: "actions",
    label: "Actions",
    sortable: false,
    thStyle: { minWidth: "120px" },
    class: "text-center"
  }
];

/** Passenger view table column definitions without actions.
 * Used to render passenger lists in passenger view mode.
 * @type {Object[]}
 */
export const FIELDS_PASSENGER_VIEW = [
  {
    key: "fullname",
    label: "Full Name",
    sortable: true,
    thStyle: { minWidth: "150px" },
    class: "text-left"
  },
  {
    key: "phone",
    label: "Phone",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left"
  },
  {
    key: "email",
    label: "Email",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left"
  },
  {
    key: "type",
    label: "Type",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center"
  },
  {
    key: "itinerary",
    label: "Flight N°",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center"
  },
  {
    key: "notify",
    label: "Notifications",
    sortable: true,
    thStyle: { minWidth: "120px" },
    class: "text-center"
  }
];

/** Ticket table column definitions.
 * Used to render ticket lists in reservation details.
 * @type {Object[]}
 */
export const TICKET_TABLE_HEADERS = [
  {
    key: "ticketid",
    label: "ID",
    sortable: false,
    thStyle: { minWidth: "100px" },
    class: "text-left",
    settings: {
      drag: false,
      disabled: true,
      default: true
    }
  },
  {
    key: "type",
    label: "type",
    sortable: false,
    thStyle: { minWidth: "120px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true
    }
  },
  {
    key: "price",
    label: "price",
    sortable: false,
    thStyle: { minWidth: "150px" },
    class: "alinmov7",
    settings: {
      drag: true,
      disabled: true,
      default: true
    }
  },
  {
    key: "status",
    label: "Status",
    sortable: false,
    thStyle: { minWidth: "120px" },
    class: "alinmov6",
    settings: {
      drag: true,
      disabled: false,
      default: true
    }
  }
];